import React, { useContext, useEffect, useRef, useState } from "react";

import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

import AddTemplateModal from "../../common/AddTemplateModal/AddTemplateModal";
import CfLoader from "../../common/CfLoader/CfLoader";
import Toast from "../../common/Toast/Toast";
import ProgrammeBatchCard from "../../components/Cards/ProgrammeBatchCard/ProgrammeBatchCard";
import EntityConstants from "../../configs/EntityConstants";
import { TEMPLATE_TYPE } from "../../configs/templateTypes";
import useHtmlDesignsEnabled from "../../hooks/useHtmlDesignsEnabled";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import FilterIcon from "../../resources/icons/filter.svg";
import PlusCircle from "../../resources/icons/plus-circle.svg";
import CalculateCardsPerRow from "../../service/CalculateCardsPerRow";
import { DEL, GET } from "../../service/CRUDService";
import AuthContext from "../../store/auth-context";
import useNavigationStore from "../../store/navigationStore";
import { pageRootTestIds } from "../../testing/pageRootTestIds";

const WorkspaceCertificates = () => {
  const authContext = useContext(AuthContext);
  const htmlDesignsEnabled = useHtmlDesignsEnabled();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentProgramme = useNavigationStore(
    (state) => state.currentProgramme,
  );
  const currentBatch = useNavigationStore((state) => state.currentBatch);

  const [isLoadingCredTemplates, setIsLoadingCredTemplates] = useState(false);
  const [cardWidth] = useState("28rem");
  const [gap] = useState("1rem");
  const containerRef = useRef(null);
  const [cardsPerRow, setCardsPerRow] = useState(0);
  const rowsPerPage = 2;
  const pageSize = cardsPerRow > 0 ? cardsPerRow * rowsPerPage : 0;
  const [countRecentList, setCountRecentList] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [searchNext, setSearchNext] = useState(null);
  const [searchPrevious, setSearchPrevious] = useState(null);
  const [recentList, setRecentList] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [templateType, setTemplateType] = useState("All Templates");
  const pendingDeletionIdRef = useRef(null);
  const previousTemplateTypeRef = useRef(templateType);

  const buildTemplatesUrl = ({ limit, offset, search = "" }) => {
    const queryParams = new URLSearchParams({
      batch_code: currentBatch.batch_code,
      limit: String(limit),
      offset: String(offset),
    });

    if (templateType !== "All Templates") {
      queryParams.set("issuer_template_type", templateType);
    }

    if (search.trim()) {
      queryParams.set("search", search.trim());
    }

    return `${EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES}?${queryParams.toString()}`;
  };

  useEffect(() => {
    if (!currentProgramme || !currentBatch) {
      navigate("/issuer/workspace", { replace: true });
    }
  }, [currentProgramme, currentBatch, navigate]);

  const GET_ISSUER_RECENT = async (limit, offset) => {
    if (!currentBatch || limit <= 0) {
      setIsLoadingCredTemplates(false);
      return;
    }
    setIsLoadingCredTemplates(true);
    const apiUrl = buildTemplatesUrl({ limit, offset });

    try {
      const response = await GET(apiUrl);
      const data = await response.json();

      if (data.error) {
        console.log(data.error);
        return;
      }

      setNext(data.data.next);
      setPrevious(data.data.previous);
      setCountRecentList(data.data.count);
      setRecentList(data.data.results);
      authContext.DraftOffsetHandler(offset);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingCredTemplates(false);
    }
  };

  const deleteCertificateTemplate = async (issuer_credential_template_id) => {
    try {
      const body = {
        issuer_credential_template_id: issuer_credential_template_id,
      };
      const response = await DEL(
        EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES,
        body,
      );

      const data = await response.json();

      if (response.ok) {
        pendingDeletionIdRef.current = issuer_credential_template_id;
        removeCredentialFromList(issuer_credential_template_id);

        const activeList = searchTerm !== "" ? searchResults : recentList;
        const refreshOffset =
          activeList.length <= 1 && authContext.draftOffset > 0
            ? Math.max(0, authContext.draftOffset - pageSize)
            : authContext.draftOffset;

        if (searchTerm.trim() !== "") {
          await performSearchAPI(searchTerm, undefined, refreshOffset);
        } else {
          await GET_ISSUER_RECENT(pageSize, refreshOffset);
        }

        Toast({
          type: "success",
          title: t("workspace:success"),
          message: t("workspace:certificate_delete_successful"),
        });
      } else {
        Toast({
          type: "error",
          title: t("workspace:error"),
          message: data.error || t("workspace:failed_delete_certificate"),
        });
      }
    } catch (error) {
      console.error("Failed to delete certificate:", error);
      Toast({
        type: "error",
        title: t("workspace:error"),
        message: t("workspace:failed_delete_certificate"),
      });
    }
  };

  const performSearchAPI = async (searchTerm, signal, offset = 0) => {
    try {
      if (!currentBatch) {
        setSearchResults([]);
        setSearchCompleted(true);
        setIsSearching(false);
        return;
      }

      const apiUrl = buildTemplatesUrl({
        limit: pageSize,
        offset,
        search: searchTerm,
      });

      const response = await GET(apiUrl, { signal });
      const data = await response.json();

      if (data.error) {
        console.log(data.error);
        setSearchResults([]);
        setSearchNext(null);
        setSearchPrevious(null);
      } else {
        setSearchResults(data.data.results);
        setCountRecentList(data.data.count);
        setSearchNext(data.data.next);
        setSearchPrevious(data.data.previous);
        authContext.DraftOffsetHandler(offset);
      }
      setSearchCompleted(true);
    } catch (error) {
      if (error.name === "AbortError") {
        throw error;
      }

      console.error("Error during search:", error);
      setSearchResults([]);
      setSearchNext(null);
      setSearchPrevious(null);
      setSearchCompleted(true);
    } finally {
      if (!signal?.aborted) {
        setIsSearching(false);
      }
    }
  };

  const {
    handleSearch,
    cancelPendingRequest,
    cleanup: cleanupSearch,
  } = useSearchDebounce(performSearchAPI, 500);

  const handleSearchInputChange = (e) => {
    const term = e.target.value ? e.target.value : "";
    setSearchTerm(term);
    authContext.DraftOffsetHandler(0);

    if (term === "") {
      cleanupSearch();
      setIsSearching(false);
      setSearchResults([]);
      setSearchCompleted(false);
      GET_ISSUER_RECENT(pageSize, 0);
      return;
    }

    if (!currentBatch) {
      setIsSearching(false);
      setSearchResults([]);
      setSearchCompleted(true);
      return;
    }

    setSearchResults([]);
    cancelPendingRequest();
    setIsSearching(true);
    setSearchCompleted(false);
    handleSearch(term, {
      onStart: () => setIsSearching(true),
      onComplete: () => setIsSearching(false),
      onError: () => {
        setSearchResults([]);
        setSearchNext(null);
        setSearchPrevious(null);
        setSearchCompleted(true);
        setIsSearching(false);
      },
      minChars: 1,
    });
  };

  useEffect(() => {
    const needsRefresh = sessionStorage.getItem("workspace_needs_refresh");
    if (needsRefresh === "true") {
      sessionStorage.removeItem("workspace_needs_refresh");
      setRefreshKey((prev) => prev + 1);
    }
  }, []);

  useEffect(() => {
    if (cardsPerRow > 0 && currentBatch) {
      GET_ISSUER_RECENT(pageSize, 0);
    }
  }, [templateType, cardsPerRow, refreshKey, currentBatch]);

  useEffect(() => {
    if (previousTemplateTypeRef.current === templateType) {
      return;
    }

    previousTemplateTypeRef.current = templateType;
    authContext.DraftOffsetHandler(0);

    if (searchTerm.trim() !== "" && currentBatch && pageSize > 0) {
      setSearchResults([]);
      setIsSearching(true);
      setSearchCompleted(false);
      performSearchAPI(searchTerm, undefined, 0);
    }
  }, [templateType, searchTerm, currentBatch, pageSize]);

  const handlePrevious = () => {
    const newOffset = Math.max(0, authContext.draftOffset - pageSize);
    if (searchTerm) {
      setIsSearching(true);
      setSearchCompleted(false);
      performSearchAPI(searchTerm, undefined, newOffset);
    } else {
      GET_ISSUER_RECENT(pageSize, newOffset);
    }
  };

  const handleNext = () => {
    const newOffset = authContext.draftOffset + pageSize;
    if (searchTerm) {
      setIsSearching(true);
      setSearchCompleted(false);
      performSearchAPI(searchTerm, undefined, newOffset);
    } else {
      GET_ISSUER_RECENT(pageSize, newOffset);
    }
  };

  const refreshTemplates = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const removeCredentialFromList = (credentialId) => {
    const removeAndEnsure = (list, setList, count) => {
      setList((prev) => {
        const updatedList = prev.filter(
          (item) => item.issuer_credential_template_id !== credentialId,
        );
        if (currentBatch && credentialId !== pendingDeletionIdRef.current) {
          ensureFullRow(updatedList, setList, count);
        }
        return updatedList;
      });
    };

    const ensureFullRow = (list, setList, count) => {
      if (list.length < pageSize && count > list.length && currentBatch) {
        const offset = list.length;
        GET(
          `${
            EntityConstants.GET_ISSUER_CREDENTIAL_BATCH_TEMPLATES
          }?batch_code=${currentBatch.batch_code}&limit=${
            pageSize - list.length
          }&offset=${offset}`,
        )
          .then((response) => response.json())
          .then((data) => {
            if (!data.error && data.data.results.length > 0) {
              setList((prev) => [...prev, ...data.data.results]);
            }
          })
          .catch((error) => {
            console.error("Error fetching additional certificates:", error);
          });
      }
    };

    if (credentialId !== pendingDeletionIdRef.current) {
      removeAndEnsure(searchResults, setSearchResults, countRecentList);
      removeAndEnsure(recentList, setRecentList, countRecentList);
    } else {
      setSearchResults((prev) =>
        prev.filter(
          (item) => item.issuer_credential_template_id !== credentialId,
        ),
      );
      setRecentList((prev) =>
        prev.filter(
          (item) => item.issuer_credential_template_id !== credentialId,
        ),
      );
      setTimeout(() => {
        pendingDeletionIdRef.current = null;
      }, 100);
    }
  };

  const handleBatchClick = () => {
    navigate(`/issuer/workspace/batches/credentials`);
  };

  const handleProgrammeClick = () => {
    navigate(`/issuer/workspace/batches/${currentProgramme?.programme_code}`);
  };
  const handleWorkspaceClick = () => {
    navigate("/issuer/workspace");
  };

  useEffect(() => {
    const updateCardsPerRow = () => {
      if (containerRef.current) {
        const calculatedCardsPerRow = CalculateCardsPerRow(
          containerRef,
          cardWidth,
          gap,
        );
        const capped = Math.min(calculatedCardsPerRow, 3);
        setCardsPerRow(capped < 1 ? 1 : capped);
      }
    };

    updateCardsPerRow();
    window.addEventListener("resize", updateCardsPerRow);
    return () => window.removeEventListener("resize", updateCardsPerRow);
  }, [cardWidth, gap]);

  useEffect(() => {
    authContext.lastVisitedPathHandler(location.pathname);
  }, [location.pathname, authContext]);

  const currentNext = searchTerm !== "" ? searchNext : next;
  const currentPrevious = searchTerm !== "" ? searchPrevious : previous;

  if (!currentProgramme || !currentBatch) {
    return (
      <div className="w-full mt-20 bg-cfSecondary min-h-screen flex justify-center items-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div
      className="w-full mt-20 bg-cfSecondary min-h-screen"
      id={pageRootTestIds.workspaceCertificates}
    >
      <div className="">
        <div
          className={`w-full bg-cfSearchContainer flex justify-center py-[0.1rem] px-4`}
        >
          <div
            className={`flex flex-col lg:flex-row items-stretch lg:items-center justify-between w-full gap-2 lg:gap-4`}
          >
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-2 lg:gap-4 min-w-0 w-full lg:w-auto">
              <div
                className={`flex flex-col lg:flex-row items-start lg:items-center min-h-[2.5rem] lg:min-h-[4rem] justify-between w-full min-w-0`}
              >
                <div className="min-w-0 w-full">
                  <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
                    <h2 className="text-lg font-medium flex items-center justify-start min-h-[2.5rem] lg:min-h-[4rem] w-full min-w-0">
                      <div className="flex items-center gap-2 text-sm text-gray-500 truncate min-w-0 max-w-full">
                        <button
                          onClick={handleWorkspaceClick}
                          id="workspace-certificates-breadcrumb-workspace"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleWorkspaceClick();
                            }
                          }}
                          className="hover:text-gray-800 cursor-pointer focus:outline-none"
                        >
                          {t("workspace:workspace")}
                        </button>
                        <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                        <button
                          onClick={handleProgrammeClick}
                          id="workspace-certificates-breadcrumb-programme"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleProgrammeClick();
                            }
                          }}
                          className="hover:text-gray-800 cursor-pointer focus:outline-none breadcrumb-item"
                          title={
                            currentProgramme.programme_name ||
                            t("workspace:programme")
                          }
                        >
                          {currentProgramme.programme_name ||
                            t("workspace:programme")}
                        </button>
                        <ChevronRight className="h-4 w-4 text-cfTextBreadcrumbDisabled" />
                        <button
                          onClick={handleBatchClick}
                          id="workspace-certificates-breadcrumb-batch"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleBatchClick();
                            }
                          }}
                          className="hover:text-gray-800 cursor-pointer focus:outline-none text-gray-800 font-medium breadcrumb-item"
                          title={
                            currentBatch.batch_name || t("workspace:batch")
                          }
                        >
                          {currentBatch.batch_name || t("workspace:batch")}
                        </button>
                      </div>
                    </h2>
                  </div>
                </div>
              </div>
              <div className="border border-cfSignInSignUpInputBorder px-2 py-1 rounded-md bg-cfSecondary flex items-center gap-2 w-full lg:w-auto">
                <img src={FilterIcon} alt="" className="w-4 h-4" />
                <select
                  value={templateType}
                  onChange={(e) => setTemplateType(e.target.value)}
                  id="workspace-certificates-filter-template-type"
                  className="px-3 py-1 w-full lg:w-[13rem] border-none rounded-md text-sm focus:outline-none"
                >
                  <option value="All Templates">
                    {t("workspace:all_certificates")}
                  </option>
                  <option value={TEMPLATE_TYPE.SVG}>SVG</option>
                  <option value={TEMPLATE_TYPE.LEGACY}>Legacy</option>
                  {htmlDesignsEnabled && (
                    <option value={TEMPLATE_TYPE.HTML}>HTML</option>
                  )}
                </select>
              </div>
            </div>
            {isModalOpen && (
              <AddTemplateModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                programmeId={currentProgramme.programme_code}
                programmeName={currentProgramme.programme_name}
                batchId={currentBatch.batch_code}
                batchName={currentBatch.batch_name}
                onTemplateAdded={refreshTemplates}
              />
            )}
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center lg:justify-end lg:mt-0 gap-2 lg:gap-4 w-full lg:w-auto">
              <button
                onClick={() => setIsModalOpen(true)}
                id="workspace-certificates-add-template"
                className="py-2 w-full lg:w-auto lg:min-w-[11rem] px-2 gap-2 bg-cfPrimaryButton text-cfSecondary rounded-[8px] flex items-center justify-center hover:bg-cfPrimaryDark transition duration-200 border border-gray-800"
              >
                <img src={PlusCircle} className="h-4 w-4" alt="" />
                <span className="font-medium">
                  {t("workspace:add_certificates")}
                </span>
              </button>
              <form
                onSubmit={(e) => e.preventDefault()}
                className="bg-cfSecondary p-2 rounded-lg w-full lg:w-[25rem]"
              >
                <input
                  type="text"
                  name="search"
                  id="workspace-certificates-input-search"
                  onChange={handleSearchInputChange}
                  placeholder={t("workspace:search_certificates")}
                  className="outline-none border-none p-0 placeholder:text-sm w-full"
                  style={{ fontSize: "0.875rem", lineHeight: "1.25rem" }}
                  value={searchTerm}
                />
              </form>
            </div>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="w-full px-4 pb-4">
        {searchTerm !== "" ? (
          <>
            <div
              className="mt-[1rem]"
              style={{
                display: "grid",
                gridTemplateColumns:
                  cardsPerRow > 0
                    ? `repeat(${cardsPerRow}, minmax(0, 1fr))`
                    : "repeat(1, minmax(0, 1fr))",
                gap: gap,
              }}
            >
              {searchResults.map(
                ({
                  issuer_credential_template_id,
                  credential_template_id,
                  certificate_name,
                  preview_image,
                  created_at,
                  updated_at,
                  description,
                  tagsList,
                  template_type,
                  thumbnail_webp_url,
                  is_deletable,
                }) => (
                  <ProgrammeBatchCard
                    key={issuer_credential_template_id}
                    name={certificate_name}
                    credential_template_id={credential_template_id}
                    issuer_credential_template_id={
                      issuer_credential_template_id
                    }
                    created_at={created_at}
                    lastEdited={updated_at}
                    thumbnail_image={preview_image}
                    description={description}
                    tagsList={tagsList}
                    template_type={template_type}
                    navigate_url={`/issuer/workspace/batches/summary/${credential_template_id}/${issuer_credential_template_id}?templateType=${template_type}`}
                    deleteCertificateTemplate={deleteCertificateTemplate}
                    thumbnail_webp_url={thumbnail_webp_url}
                    programmeCode={currentProgramme.programme_code}
                    batchCode={currentBatch.batch_code}
                    isDeletable={is_deletable}
                  />
                ),
              )}
              {isSearching && searchResults.length === 0 && (
                <div className="col-span-full w-full flex justify-center items-center py-4">
                  <CfLoader />
                </div>
              )}
              {!isSearching &&
                searchCompleted &&
                searchResults.length === 0 && (
                  <div className="col-span-full mt-6 w-full flex justify-center">
                    <p
                      id="workspace-certificates-search-no-results"
                      className="text-lg font-semibold text-cfRootBgColor"
                    >
                      {t("workspace:certificates_not_found")}
                    </p>
                  </div>
                )}
            </div>
            <div className="flex items-center justify-between mt-6 w-full pb-8">
              <button
                onClick={handlePrevious}
                id="workspace-certificates-pagination-previous"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-cfCardTagText capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={currentPrevious === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>
                  <span>{t("workspace:previous")}</span>
                </span>
              </button>

              <button
                onClick={handleNext}
                id="workspace-certificates-pagination-next"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-cfCardTagText capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={currentNext === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>{t("workspace:next")}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </>
        ) : (
          <>
            {isLoadingCredTemplates ? (
              <div className="w-full flex justify-center items-center py-12">
                <CfLoader />
              </div>
            ) : (
              <div>
                <div
                  className="mt-[1rem]"
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      cardsPerRow > 0
                        ? `repeat(${cardsPerRow}, minmax(0, 1fr))`
                        : "repeat(1, minmax(0, 1fr))",
                    gap: gap,
                  }}
                >
                  {recentList.map(
                    ({
                      issuer_credential_template_id,
                      credential_template_id,
                      certificate_name,
                      preview_image,
                      created_at,
                      updated_at,
                      description,
                      tagsList,
                      template_type,
                      thumbnail_webp_url,
                      is_deletable,
                    }) => (
                      <ProgrammeBatchCard
                        key={issuer_credential_template_id}
                        name={certificate_name}
                        credential_template_id={credential_template_id}
                        issuer_credential_template_id={
                          issuer_credential_template_id
                        }
                        created_at={created_at}
                        lastEdited={updated_at}
                        thumbnail_image={preview_image}
                        description={description}
                        tagsList={tagsList}
                        template_type={template_type}
                        navigate_url={`/issuer/workspace/batches/summary/${credential_template_id}/${issuer_credential_template_id}?templateType=${template_type}`}
                        deleteCertificateTemplate={deleteCertificateTemplate}
                        programmeCode={currentProgramme.programme_code}
                        batchCode={currentBatch.batch_code}
                        thumbnail_webp_url={thumbnail_webp_url}
                        isDeletable={is_deletable}
                      />
                    ),
                  )}
                </div>
              </div>
            )}

            {recentList.length === 0 && !isLoadingCredTemplates && (
              <div className="mt-6 w-full flex justify-center">
                <p
                  id="workspace-certificates-no-certificates"
                  className="text-lg font-semibold text-cfRootBgColor"
                >
                  {t("workspace:no_certificates_found")}
                </p>
              </div>
            )}

            <div className="flex items-center justify-between mt-6 w-full pb-8">
              <button
                onClick={handlePrevious}
                id="workspace-certificates-pagination-previous"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-cfCardTagText capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={currentPrevious === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                    />
                  </svg>
                  <span>{t("workspace:previous")}</span>
                </span>
              </button>

              <button
                onClick={handleNext}
                id="workspace-certificates-pagination-next"
                className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-cfCardTagText capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
                disabled={currentNext === null}
              >
                <span className="flex items-center justify-center gap-2">
                  <span>{t("workspace:next")}</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-5 h-5 rtl:-scale-x-100"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                    />
                  </svg>
                </span>
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WorkspaceCertificates;
